// /Users/boyd/Dev/freegopos/einvapp/src/context/LocationContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from 'react';
import { ApiCountry, ApiState, ApiCity } from '../utils/types';
// Import the new fetch functions
import { fetchLocations, fetchStatesForCountry, fetchCitiesForState } from '../utils/api';

interface LocationContextProps {
    countries: ApiCountry[];
    states: ApiState[]; // Holds states for the currently selected country
    cities: ApiCity[]; // Holds cities for the currently selected state
    loadingCountries: boolean;
    loadingStates: boolean;
    loadingCities: boolean;
    error: Error | null; // Consolidated error state for simplicity
    loadStates: (countryId: string | number) => Promise<void>;
    loadCities: (stateId: string | number) => Promise<void>;
    findCountryByName: (name?: string | null) => ApiCountry | undefined;
    // findStateByName and findCityByName will now search the 'states' and 'cities' arrays
    findStateByName: (stateName?: string | null) => ApiState | undefined;
    findCityByName: (cityName?: string | null) => ApiCity | undefined;
    clearStates: () => void; // Function to clear states/cities when country changes
    clearCities: () => void; // Function to clear cities when state changes
}

const LocationContext = createContext<LocationContextProps | undefined>(undefined);

export const LocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [countries, setCountries] = useState<ApiCountry[]>([]);
    const [states, setStates] = useState<ApiState[]>([]); // State for current states
    const [cities, setCities] = useState<ApiCity[]>([]); // State for current cities
    const [loadingCountries, setLoadingCountries] = useState<boolean>(true);
    const [loadingStates, setLoadingStates] = useState<boolean>(false); // Initially false
    const [loadingCities, setLoadingCities] = useState<boolean>(false); // Initially false
    const [error, setError] = useState<Error | null>(null);

    // Fetch initial countries on mount
    useEffect(() => {
        const loadInitialLocations = async () => {
            setLoadingCountries(true);
            setError(null);
            try {
                const data = await fetchLocations();
                setCountries(data);
            } catch (err) {
                setError(err instanceof Error ? err : new Error('Failed to load country data'));
                console.error(err);
            } finally {
                setLoadingCountries(false);
            }
        };
        loadInitialLocations();
    }, []);

    // Function to load states for a country
    const loadStates = useCallback(async (countryId: string | number) => {
        setLoadingStates(true);
        setStates([]); // Clear previous states
        setCities([]); // Clear cities as well
        setError(null);
        try {
            const data = await fetchStatesForCountry(countryId);
            setStates(data);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to load state data'));
            console.error(err);
            setStates([]); // Ensure states is empty on error
        } finally {
            setLoadingStates(false);
        }
    }, []);

    // Function to load cities for a state
    const loadCities = useCallback(async (stateId: string | number) => {
        setLoadingCities(true);
        setCities([]); // Clear previous cities
        setError(null);
        try {
            const data = await fetchCitiesForState(stateId);
            setCities(data);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to load city data'));
            console.error(err);
            setCities([]); // Ensure cities is empty on error
        } finally {
            setLoadingCities(false);
        }
    }, []);

    // Helper to clear states and cities (e.g., when country changes)
    const clearStates = useCallback(() => {
        setStates([]);
        setCities([]);
    }, []);

    // Helper to clear cities (e.g., when state changes)
    const clearCities = useCallback(() => {
        setCities([]);
    }, []);


    // --- Find functions (Wrap in useCallback) ---
    const findCountryByName = useCallback((name?: string | null): ApiCountry | undefined => {
        if (!name || !Array.isArray(countries)) return undefined;
        const searchName = name.trim().toLowerCase();
        return countries.find(c => c.name.trim().toLowerCase() === searchName);
    }, [countries]); // Add countries as dependency

    // Searches the currently loaded 'states' array
    const findStateByName = useCallback((stateName?: string | null): ApiState | undefined => {
        if (!stateName || !Array.isArray(states)) return undefined;
        const searchName = stateName.trim().toLowerCase();
        return states.find(s => s.name.trim().toLowerCase() === searchName);
    }, [states]); // Add states as dependency

    // Searches the currently loaded 'cities' array
    const findCityByName = useCallback((cityName?: string | null): ApiCity | undefined => {
        if (!cityName || !Array.isArray(cities)) return undefined;
        const searchName = cityName.trim().toLowerCase();
        return cities.find(city => city.name.trim().toLowerCase() === searchName);
    }, [cities]); // Add cities as dependency
    // --- End Find functions ---

    return (
        <LocationContext.Provider value={{
            countries,
            states, // Provide states
            cities, // Provide cities
            loadingCountries,
            loadingStates, // Provide loading states
            loadingCities,
            error,
            loadStates, // Provide load functions
            loadCities,
            findCountryByName, // Now provides memoized version
            findStateByName,   // Now provides memoized version
            findCityByName,    // Now provides memoized version
            clearStates,
            clearCities
        }}>
            {children}
        </LocationContext.Provider>
    );
};

// useLocations hook remains the same
export const useLocations = (): LocationContextProps => {
    const context = useContext(LocationContext);
    if (context === undefined) {
        throw new Error('useLocations must be used within a LocationProvider');
    }
    return context;
};
